import React, { useState, useEffect } from 'react'
import { FiSearch, FiDownload } from 'react-icons/fi'
import {
  Box,
  Button,
  Chip,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material'
import api from '@/services/api'
import CardContent from '@mui/material/CardContent'
import Card from '@mui/material/Card'
import CheckIcon from '@mui/icons-material/Check'
import PauseIcon from '@mui/icons-material/Pause'
import CancelIcon from '@mui/icons-material/Cancel'
import Notfound from '../../../Notfound'
import { TableShimmer } from '../../../ShimmerUI'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import SubscriptionsIcon from '@mui/icons-material/Subscriptions'
import RowsPerPageDropdown from '../RowsPerPageDropdown'
import dayjs from 'dayjs'
const SubscriptionManagementPage: React.FC = () => {
  const perPage = 10
  const prevSize = sessionStorage.getItem('prevSize')
  const ourPageSize = prevSize ? parseInt(prevSize, 10) : 10
  const [pageSize, setPageSize] = useState(10)
  const [services, setServices] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [subscriptions, setSubscriptions] = useState<any[]>([])

  const [subscriptionId, setSubscriptionId] = useState('')
  const [planName, setPlanName] = useState('')
  const [selectedAccountStatus, setSelectedAccountStatus] = useState('')
  const [providerName, setProviderName] = useState('')
  const [planTypeFilter, setPlanTypeFilter] = useState('All')
  const [statusFilter, setStatusFilter] = useState('All')
  const [planType, setPlanType] = useState('')
  const [loading, setLoading] = useState(false)
  const [subscriptionTotal, setSubscriptionTotal] = useState(0)
  const [subscriptionActive, setSubscriptionActive] = useState(0)
  const [subscriptionExpired, setSubscriptionExpired] = useState(0)
  const [subscriptionCancelled, setSubscriptionCancelled] = useState(0)
  const [loadingForShimmer, setLoadingForShimmer] = useState(true)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [selectedSubscription, setSelectedSubscription] = useState<any>(null)
  const [cancelling, setCancelling] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const formatDateMMDDYY = (date: string | Date) => {
    const d = new Date(date)
    const month = d.getMonth() + 1 // months are zero-based, so add 1
    const day = d.getDate()
    const year = d.getFullYear().toString().slice(-4) // Get the last two digits of the year

    // Format as MM-DD-YY
    return `${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}-${year}`
  }
  const handleAccountStatusChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedAccountStatus(event.target.value)
  }
  const handlePlanTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPlanType(e.target.value)
  }

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page)
    fetchSubscriptions(page)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    sessionStorage.setItem('prevSize', newPageSize.toString())
  }
  // Function to fetch subscriptions based on form input parameters
  const fetchSubscriptions = async (page?: number) => {
    setLoading(true)
    try {
      setLoadingForShimmer(true)
      const params: Record<string, any> = {
        page: page || 1, // Default to 1 if page is not provided
        limit: 10,
        accountStatus: selectedAccountStatus,
      }

      // Add filter parameters based on user input
      if (subscriptionId) {
        params.subscriptionId = subscriptionId.trim()
      }
      if (planName) {
        params.planName = planName.trim()
      }
      if (providerName) {
        params.providerName = providerName
      }
      if (planType) {
        params.planType = planType
      }

      const response = await api.get('membership/get-subscriptions', { params })

      setSubscriptions(response.data.usermembership || [])
      setSubscriptionTotal(response.data.totalRecords || 0)
      setSubscriptionActive(response.data.active || 0)
      setSubscriptionExpired(response.data.cancelled || 0)
      setSubscriptionCancelled(response.data.cancelled || 0)
      setTotalPages(Math.ceil(response?.data?.totalRecords / pageSize))
    } catch (error) {
      console.error('Error fetching subscriptions:', error)
      // Handle error appropriately (e.g., show toast or alert)
    } finally {
      setLoadingForShimmer(false)
      setLoading(false)
    }
  }
  const handleReset = () => {
    setSubscriptionId('') // Reset Subscription ID
    setPlanName('') // Reset Plan Name
    setProviderName('') // Reset Provider Name
    setPlanType('') // Reset Plan Type
    setSelectedAccountStatus('') // Reset Account Status
  }

  const handleCancelClick = (subscription: any) => {
    setSelectedSubscription(subscription)
    setCancelDialogOpen(true)
  }

  const handleCancelConfirm = async () => {
    if (!selectedSubscription?.subscriptionId) {
      toast.error('Invalid subscription ID')
      return
    }

    setCancelling(true)
    try {
      const subscriptionId = selectedSubscription.subscriptionId
      await api.put(`/membership/cancel-membership-plan/${subscriptionId}`)
      toast.success('Subscription cancelled successfully')
      setCancelDialogOpen(false)
      setSelectedSubscription(null)
      // Refresh the subscriptions list
      fetchSubscriptions(currentPage)
    } catch (error: any) {
      console.error('Error cancelling subscription:', error)
      toast.error(
        error?.response?.data?.message ||
          'Failed to cancel subscription. Please try again.'
      )
    } finally {
      setCancelling(false)
    }
  }

  const handleCancelDialogClose = () => {
    if (!cancelling) {
      setCancelDialogOpen(false)
      setSelectedSubscription(null)
    }
  }

  const handleExport = async () => {
    setExportLoading(true)
    try {
      const params: Record<string, any> = {}

      // Add filter parameters based on current filters
      if (selectedAccountStatus) {
        params.accountStatus = selectedAccountStatus
      }
      if (subscriptionId?.trim()) {
        params.subscriptionId = subscriptionId.trim()
      }
      if (planName?.trim()) {
        params.planName = planName.trim()
      }
      if (providerName?.trim()) {
        params.providerName = providerName.trim()
      }
      if (planType) {
        params.planType = planType
      }

      const response = await api.get('membership/get-membership-export', {
        responseType: 'blob',
        params,
      })

      // Create a blob from the response
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'text/csv',
      })

      // Create a download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition']
      let filename = 'subscriptions_export.csv'
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }

      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.success('Export completed successfully')
    } catch (error) {
      console.error('Error exporting subscriptions:', error)
      toast.error('Failed to export subscriptions data')
    } finally {
      setExportLoading(false)
    }
  }

  useEffect(() => {
    fetchSubscriptions()
  }, [subscriptionId, planName, providerName, selectedAccountStatus, planType])
  return (
    <div className="p-6 mx-auto max-w-7xl">
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-4 mb-6 sm:flex-row sm:items-center">
        <h1 className="text-2xl font-semibold text-[#6E7B8B]">
          Subscription Management
        </h1>
        {/* <button className="flex items-center gap-2 px-4 py-2 text-white bg-[#5BC0BE] border border-gray-300 rounded-lg hover:bg-[#49a9a7]">
          <FiDownload className="w-4 h-4" />
          Export CSV
        </button> */}
        <button
          className="flex items-center gap-2 px-4 py-2 text-white bg-[#5BC0BE] border border-gray-300 rounded-lg hover:bg-[#49a9a7] disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleExport}
          disabled={exportLoading}
          style={{
            cursor: exportLoading ? 'not-allowed' : 'pointer',
          }}
        >
          <FiDownload className="w-4 h-4" />
          {exportLoading ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      {/* Filters Row */}
      <Card
        sx={{
          height: '100%',
          cursor: 'pointer',
          boxShadow: 2,
          padding: 2,
          mb: 3,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 4,
          },
        }}
      >
        <CardContent>
          <div className="grid items-end grid-cols-1 gap-4 md:grid-cols-5">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Subscription ID
              </label>
              <input
                type="text"
                value={subscriptionId}
                onChange={e => setSubscriptionId(e.target.value)}
                placeholder="Subscription ID"
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#026730] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Plan name
              </label>
              <input
                type="text"
                value={planName}
                onChange={e => setPlanName(e.target.value)}
                placeholder="Plan name"
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#026730] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Provider Name
              </label>
              <input
                type="text"
                value={providerName}
                onChange={e => setProviderName(e.target.value)}
                placeholder="Provider Name"
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#026730] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Plan Type
              </label>
              <select
                value={planType}
                onChange={handlePlanTypeChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#026730] focus:border-transparent"
              >
                <option value="">Plan Type</option>
                <option value="Monthly">Monthly</option>
                <option value="Yearly">Yearly</option>
              </select>
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Account Status
              </label>
              <select
                id="accountStatus"
                value={selectedAccountStatus}
                onChange={handleAccountStatusChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#026730] focus:border-transparent"
              >
                <option value="" disabled selected>
                  Select Status
                </option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            {/* Reset Button placed just below Account Status */}
            <div className="flex items-start justify-start mt-2 md:col-span-5">
              <Box display="flex" alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleReset} // Uncomment this when implementing reset logic
                  sx={{
                    borderRadius: 2,
                    width: '113px',
                  }}
                >
                  Reset All
                </Button>
              </Box>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow h-35">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {subscriptionTotal}
            </h2>
            <p className="text-sm text-gray-500">Total Subscriptions</p>
          </div>
          <div className="flex items-center justify-center w-12 h-12 text-xl font-bold text-green-600 bg-green-100 rounded-full">
            <SubscriptionsIcon />
          </div>
        </div>
        <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {subscriptionActive}
            </h2>
            <p className="text-sm text-gray-500">Active Subscriptions</p>
          </div>
          <div className="flex items-center justify-center w-12 h-12 text-xl font-bold text-blue-600 bg-blue-100 rounded-full">
            <CheckIcon />
          </div>
        </div>
        <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {subscriptionExpired}
            </h2>
            <p className="text-sm text-gray-500">Inactive Subscriptions</p>
          </div>
          <div className="flex items-center justify-center w-12 h-12 text-xl font-bold text-red-600 bg-red-100 rounded-full">
            <PauseIcon />
          </div>
        </div>
        <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {subscriptionCancelled}
            </h2>
            <p className="text-sm text-gray-500">Cancelled Subscriptions</p>
          </div>
          <div className="flex items-center justify-center w-12 h-12 text-xl font-bold text-orange-600 bg-orange-100 rounded-full">
            <CancelIcon />
          </div>
        </div>
      </div>

      {/* Subscription Table */}
      <div className="overflow-hidden bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr className="text-sm text-left text-white uppercase bg-[#5BC0BE]">
                <th className="px-6 py-3 font-medium">Subscription ID</th>
                <th className="px-6 py-3 font-medium">Plan Name</th>
                <th className="px-6 py-3 font-medium">Provider Name</th>
                <th className="px-6 py-3 font-medium">Plan Type</th>
                <th className="px-6 py-3 font-medium">Amount</th>
                <th className="px-6 py-3 font-medium">Start Date</th>
                <th className="px-6 py-3 font-medium">End Date</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Trial Start </th>
                <th className="px-6 py-3 font-medium">Trial End </th>

                <th className="px-6 py-3 font-medium"> Trail Status</th>
                <th className="px-6 py-3 font-medium">Auto-Renew</th>
                <th className="px-6 py-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loadingForShimmer ? (
                <TableShimmer columns={9} />
              ) : subscriptions?.length === 0 ? (
                <tr>
                  <td
                    colSpan={20}
                    className="p-4 text-sm text-center text-gray-500"
                  >
                    <Notfound />
                  </td>
                </tr>
              ) : (
                subscriptions.map(sub => (
                  <tr
                    key={sub._id}
                    className="transition-colors hover:bg-gray-50"
                  >
                    <td className="px-6 py-4">{sub?.subscriptionId}</td>
                    <td className="px-6 py-4">{sub?.plan_id?.name}</td>
                    <td className="px-6 py-4">
                      {sub?.user_id?.firstName || '--'}{' '}
                      {sub?.user_id?.lastName || '--'}
                    </td>
                    <td className="px-6 py-4">
                      {sub?.subscription_frequency === 'Month'
                        ? 'Monthly'
                        : sub?.subscription_frequency === 'Year'
                          ? 'Yearly'
                          : sub?.subscription_frequency}
                    </td>
                    <td className="px-6 py-4">
                      ${sub?.price ? sub?.price.toFixed(2) : '0.00'}
                    </td>

                    <td className="px-6 py-4 w-50">
                      {sub?.start_date
                        ? dayjs(sub.start_date).isValid()
                          ? dayjs(sub.start_date).format('MM/DD/YYYY')
                          : '--'
                        : '--'}
                    </td>
                    <td className="px-6 py-4 w-50">
                      {sub?.end_date
                        ? dayjs(sub.end_date).isValid()
                          ? dayjs(sub.end_date).format('MM/DD/YYYY')
                          : '--'
                        : '--'}
                    </td>

                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${
                          sub.status === 'Active'
                            ? 'border-green-500 text-green-600'
                            : 'border-red-500 text-red-600'
                        }`}
                      >
                        {sub?.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {sub?.trial_start
                        ? dayjs(sub.trial_start).isValid()
                          ? dayjs(sub.trial_start).format('MM/DD/YYYY')
                          : '--'
                        : '--'}
                    </td>
                    <td className="px-6 py-4">
                      {sub?.trial_end
                        ? dayjs(sub.trial_end).isValid()
                          ? dayjs(sub.trial_end).format('MM/DD/YYYY')
                          : '--'
                        : '--'}
                    </td>
                    <td>
                      {sub?.is_trial_started
                        ? sub.is_trial_started.charAt(0).toUpperCase() +
                          sub.is_trial_started.slice(1)
                        : ''}
                    </td>

                    <td className="px-6 py-4">
                      {sub?.renewal_date
                        ? dayjs(sub.renewal_date).isValid()
                          ? dayjs(sub.renewal_date).format('MM/DD/YYYY')
                          : '--'
                        : '--'}
                    </td>
                    <td className="px-6 py-4">
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<CancelIcon />}
                        onClick={() => handleCancelClick(sub)}
                        disabled={
                          sub?.status === 'Cancelled' ||
                          sub?.status === 'Inactive'
                        }
                        sx={{
                          textTransform: 'none',
                          borderRadius: 1,
                        }}
                      >
                        Cancel
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex justify-center mt-3">
        <div className="flex items-center gap-4">
          <RowsPerPageDropdown
            pageSize={pageSize}
            onPageSizeChange={newPageSize => {
              handlePageSizeChange(newPageSize)
              if (ourPageSize && ourPageSize !== newPageSize) {
                setCurrentPage(1)
              }
            }}
          />
        </div>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          className="mt-2 mt-md-0"
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        />
      </div>

      {/* Cancel Subscription Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CancelIcon color="error" />
          Cancel Subscription
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to cancel this subscription?
          </Typography>
          {selectedSubscription && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Subscription ID:</strong>{' '}
                {selectedSubscription.subscriptionId}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Plan Name:</strong>{' '}
                {selectedSubscription?.plan_id?.name || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Provider:</strong>{' '}
                {selectedSubscription?.user_id?.firstName || ''}{' '}
                {selectedSubscription?.user_id?.lastName || ''}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone. The subscription will be cancelled
            immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCancelDialogClose}
            disabled={cancelling}
            variant="outlined"
          >
            No, Keep Subscription
          </Button>
          <Button
            onClick={handleCancelConfirm}
            disabled={cancelling}
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
          >
            {cancelling ? 'Cancelling...' : 'Yes, Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </div>
  )
}

export default SubscriptionManagementPage
